/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIResponseInterface,
  TransactionEMSPInitialProperties,
} from '../../interface/emsp-transaction-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import transactionApi from '../../utils/api/v2/emsp-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Parameters for fetching payments.
 */
interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Thunk to fetch all commercial transactions.
 * @param {FetchParameters} data - The parameters for the fetch request.
 * @returns {any} A promise that resolves to the API response.
 */
export const fetchAllPayments = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'commercial-transaction-management/fetchAllPayments',
  async (data: FetchParameters, { dispatch }) => {
    const apiCall = transactionApi.fetchCommercialTransactionList(removeEmptyKeys(data));

    const response = await apiCall
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt || roleData.startTime),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt || roleData.startTime)),
        id: roleData._id,
        transactionId: roleData.transactionId || roleData.externalId,
        transactionStartsAt: roleData.transactionStartsAt || roleData.startTime,
        chargingPointSn: roleData.chargingPointSn || roleData.location?.name || roleData.meterId,
        userName: roleData.userName || roleData.userCustomId,
        connector: roleData.connector !== undefined ? roleData.connector : 1,
        status: roleData.status || (roleData.isBilled ? 'processed' : 'pending'),
      })),
    };
  },
);

/**
 * Thunk to refresh all commercial transactions.
 * @param {FetchParameters} data - The parameters for the refresh request.
 * @returns {any} A promise that resolves to the API response.
 */
export const refreshAllPayments = createAsyncThunk<APIResponseInterface, FetchParameters>(
  'commercial-transaction-management/refreshAllPayments',
  async (data: FetchParameters) => {
    const response = await transactionApi.fetchCommercialTransactionList(removeEmptyKeys(data));
    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt || roleData.startTime),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt || roleData.startTime)),
        id: roleData._id,
        transactionId: roleData.transactionId || roleData.externalId,
        transactionStartsAt: roleData.transactionStartsAt || roleData.startTime,
        chargingPointSn: roleData.chargingPointSn || roleData.location?.name || roleData.meterId,
        userName: roleData.userName || roleData.userCustomId,
        connector: roleData.connector !== undefined ? roleData.connector : 1,
        status: roleData.status || (roleData.isBilled ? 'processed' : 'pending'),
      })),
    };
  },
);

/**
 * Thunk to fetch list of payment method filters.
 * @returns {any} A promise that resolves to the filter list.
 */
export const fetchListPaymentMethodFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'commercial-transaction-management/fetchListPaymentMethodFilters',
  async (_, { dispatch }) => {
    try {
      const response = await transactionApi.fetchCommercialTransactionListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for EMSP commercial transaction slice.
 */
const initialState: TransactionEMSPInitialProperties = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

/**
 * Redux slice for EMSP commercial transaction management.
 */
export const paymentSlice = createSlice({
  name: 'commercial-transaction-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updatePaymentManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disablePaymentManagementApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllPayments.pending, (state: TransactionEMSPInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPayments.fulfilled,
        (state: TransactionEMSPInitialProperties, action: { payload: APIResponseInterface }) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllPayments.rejected, (state: TransactionEMSPInitialProperties) => {
        state.loading = false;
      });
    builder.addCase(refreshAllPayments.fulfilled, (state, action) => {
      state.payments = [...action.payload.results];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchListPaymentMethodFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListPaymentMethodFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListPaymentMethodFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updatePaymentManagementFilters, disablePaymentManagementApplied, setSortData } =
  paymentSlice.actions;
export default paymentSlice.reducer;
